// Trust Third-party Details Components

/**
 * Trust Level Badge Component
 * Displays trust level with appropriate styling and description
 */
export const ApckTrustLevelBadge = {
    props: {
        level: {
            type: Number,
            default: 0
        },
        description: {
            type: String,
            default: ''
        },
        showDescription: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        trustLevelClass: function() {
            const levelClasses = {
                5: 'trust-level-critical',
                4: 'trust-level-high',
                3: 'trust-level-medium',
                2: 'trust-level-low',
                1: 'trust-level-info',
                0: 'trust-level-investigate'
            };
            return levelClasses[this.level] || 'trust-level-unknown';
        },
        trustLevelName: function() {
            const levelNames = {
                5: 'HIGHEST',
                4: 'HIGH',
                3: 'MEDIUM',
                2: 'LOW',
                1: 'INFORMATIONAL',
                0: 'INVESTIGATE'
            };
            return levelNames[this.level] || 'UNKNOWN';
        }
    },
    template: `
        <div class="trust-level-badge">
            <span :class="['trust-level', trustLevelClass]" :title="description">
                {{ level }} - {{ trustLevelName }}
            </span>
            <div v-if="showDescription && description" class="trust-description">
                {{ description }}
            </div>
        </div>
    `
};

/**
 * Country Information Component
 * Displays country flag and name from GeoIP data
 */
export const ApckCountryInfo = {
    props: {
        countryInfo: {
            type: Object,
            default: null
        }
    },
    computed: {
        flagEmoji: function() {
            if (!this.countryInfo?.iso_code) return '';
            // Convert ISO code to flag emoji using regional indicator symbols
            const code = this.countryInfo.iso_code.toUpperCase();
            return String.fromCodePoint(
                ...[...code].map(c => c.charCodeAt(0) + 127397)
            );
        }
    },
    template: `
        <div v-if="countryInfo" class="country-info">
            <span class="country-flag" :title="countryInfo.name">
                {{ flagEmoji }}
            </span>
            <ui-heading tag="span"> {{ countryInfo.name }} ({{ countryInfo.iso_code }})</ui-heading>
        </div>
    `
};

/**
 * Third-party Favicon Component
 * Displays favicon with fallback to origin URL favicon
 */
export const ApckThirdpartyFavicon = {
    props: {
        favicon: {
            type: Object,
            default: function() {
                return {};
            }
        },
        hostname: {
            type: String,
            default: ''
        },
        size: {
            type: String,
            default: '32'
        }
    },
    data: function() {
        return {
            imageError: false
        };
    },
    computed: {
        faviconUrl: function() {
            // 'a=' is invalid base64, will trigger error and show default icon if no favicon available
            return `data:${this.favicon?.contentType || 'image/x-icon'};base64,${this.favicon?.favicon_base64 || 'a='}`;
        }
    },
    methods: {
        handleImageError(event) {
            this.imageError = true;
            // Replace the img element with a Font Awesome icon
            const icon = document.createElement('i')
            icon.className = 'fa fa-globe trust-icon flex dir-column align-center justify-center'
            icon.title = event.target.title
            event.target.parentNode.replaceChild(icon, event.target)
        },
    },
    template: `
        <img
            class="trust-favicon"
            :src="faviconUrl"
            :alt="hostname + ' favicon'"
            :title="hostname"
            :width="size"
            :height="size"
            @error="handleImageError"
        />
    `
};

/**
 * Trust Instance Details Component
 * Shows detailed information about a specific trust relationship instance
 */
export const ApckTrustInstanceDetails = {
    props: {
        instance: {
            type: Object,
            required: true
        }
    },
    data: function() {
        return {
            expanded: false
        };
    },
    methods: {
        toggleExpanded: function() {
            this.expanded = !this.expanded;
        },
        trustColor(level) {
            const colors = {
                5: 'red',
                4: 'orange',
                3: 'yellow',
                2: 'green',
                1: 'blue',
                0: ''
            };
            return colors[level] || '';
        },
    },
    mounted: function() {
        console.log('Trust Instance Details mounted for instance:', this.instance);
    },
    template: `
        <ui-panel-accordion color="white" noInnerPanel>
            <template #trigger>
                <ui-layout gap="2" align="start" class="grow">
                    <ui-badge :label="String(instance.trust_level)" fill :color="trustColor(instance.trust_level)" />

                    <ui-layout gap="1" direction="column" class="text--secondary">
                        <span>{{ instance.trust_type }}</span>

                        <ui-layout align="center" gap="2" wrap>
                            <a :href="instance.source_url" target="_blank" rel="noopener noreferrer" class="trust-url" v-tooltip="'Opens source domain in a new tab'">{{ instance.source_url }}</a>
                            <ui-icon icon="fa-arrow-right" size="sm" type="secondary" />
                            <a :href="instance.target_url" target="_blank" rel="noopener noreferrer" class="trust-url" v-tooltip="'Opens third party in a new tab'">{{ instance.target_url }}</a>
                        </ui-layout>
                    </ui-layout>
                </ui-layout>
            </template>
            
            <ui-layout direction="column" class="text--secondary">
                <ui-layout direction="column" gap="2">
                    <ui-heading tag="h4" size="md" color="primary">Trust Bond Description</ui-heading>

                    <p>{{ instance.high_level_bond_description }}</p>
                </ui-layout>

                <ui-layout
                    v-if="instance.detailed_bond_description !== instance.high_level_bond_description"
                    direction="column"
                    gap="2"
                >
                    <ui-heading tag="h5" color="primary">Technical Details</ui-heading>

                    <p>{{ instance.detailed_bond_description }}</p>
                </ui-layout>

                <ui-layout v-if="instance.target_signature" direction="column" gap="2">
                    <ui-heading tag="h5" color="primary">Target Signature</ui-heading>
                    <pre>
                            <code>{{ instance.target_signature }}</code>
                        </pre>
                </ui-layout>

                <ui-layout v-if="instance.source_reference_snips" direction="column" gap="2">
                    <ui-heading tag="h5" color="primary">Source Code Reference</ui-heading>
                    <pre>
                            <code>{{ instance.source_reference_snips }}</code>
                        </pre>
                </ui-layout>

                <ui-layout
                    v-if="instance.additional_sources && instance.additional_sources.length"
                    direction="column"
                    gap="2"
                >
                    <ui-heading tag="h5" color="primary">Additional Sources</ui-heading>
                    <ui-layout direction="column" tag="ul" gap="2">
                        <li v-for="source in instance.additional_sources" :key="source">
                            <a :href="source" target="_blank" rel="noopener noreferrer" class="trust-url" v-tooltip="'Opens source domain in a new tab'">{{ source }}</a>
                        </li>
                    </ui-layout>
                </ui-layout>
            </ui-layout>
        </ui-panel-accordion>
    `
};

/**
 * Domain Relationship Component
 * Shows details about a relationship between the third-party and a specific domain
 */
export const ApckDomainRelationship = {
    components: {
        'apck-trust-level-badge': ApckTrustLevelBadge,
        'apck-country-info': ApckCountryInfo,
        'apck-thirdparty-favicon': ApckThirdpartyFavicon,
        'apck-trust-instance-details': ApckTrustInstanceDetails,
    },
    props: {
        relationship: {
            type: Object,
            required: true
        }
    },
    data: function() {
        return {
            expanded: false,
            loadingMore: false,
            allTrustDetails: [],
            currentPage: 0,
            limit: 50,
            hasMoreInstances: false
        };
    },
    mounted: function() {
        // Initialize with the trust_details from the relationship prop
        this.allTrustDetails = this.relationship.trust_details || [];
        // If we have exactly 10 or more from the sample, there might be more
        this.hasMoreInstances = this.relationship.trust_instances_count > this.allTrustDetails.length;
    },
    computed: {
        trustInstancesCount: function() {
            return this.allTrustDetails?.length || 0;
        },
        totalInstancesCount: function() {
            return this.relationship.trust_instances_count || this.allTrustDetails.length;
        },
        highestTrustLevel: function() {
            return this.relationship.highest_trust_level?.level || 0;
        },
        vulnerabilityCount: function() {
            return this.relationship.vulnerabilities?.length || 0;
        },
        trustLevelLabel() {
            return `${String(this.highestTrustLevel)} - ${this.getTrustLevelName(this.highestTrustLevel)}`
        },
        trustCountsLabel() {
            return `${ this.totalInstancesCount } trust instance${ this.totalInstancesCount !== 1 ? 's' : '' }`
        },
        vulnCountsLabel() {
            return `${ this.vulnerabilityCount } vuln${ this.vulnerabilityCount !== 1 ? 's' : '' }`
        },
        hasVulnerabilities: function() {
            return this.vulnerabilityCount > 0;
        },
    },
    methods: {
        toggleExpanded: function() {
            this.expanded = !this.expanded;
        },
        formatDate: function(dateString) {
            if (!dateString) return 'N/A';
            dateString = `${dateString}000`; // Convert to milliseconds
            dateString = parseInt(dateString);
            return new Date(dateString).toLocaleDateString();
        },
        trustColor(level) {
            const colors = {
                5: 'red',
                4: 'orange',
                3: 'yellow',
                2: 'green',
                1: 'blue',
                0: ''
            };
            return colors[level] || '';
        },
        getTrustLevelName: function(level) {
            const levelNames = {
                5: 'HIGHEST',
                4: 'HIGH',
                3: 'MEDIUM',
                2: 'LOW',
                1: 'INFORMATIONAL',
                0: 'INVESTIGATE'
            };
            return levelNames[level] || 'UNKNOWN';
        },
        loadMoreInstances: async function() {
            if (this.loadingMore || !this.hasMoreInstances) return;
            
            this.loadingMore = true;
            try {
                this.currentPage += 1;
                const response = await fetch(
                    `/trust/relationship/${this.relationship.relationship_id}/instances?page=${this.currentPage}&limit=${this.limit}`,
                    {
                        headers: {
                            'Accept': 'application/json'
                        }
                    }
                );
                
                if (!response.ok) {
                    throw new Error(`Failed to load more instances: ${response.statusText}`);
                }
                
                const data = await response.json();
                
                // On first page load, replace the sample instances to avoid duplicates
                // On subsequent pages, append to existing
                if (data.trust_instances && data.trust_instances.length > 0) {
                    if (this.currentPage === 1) {
                        // Replace the initial 10 sample instances with full paginated results
                        this.allTrustDetails = data.trust_instances;
                    } else {
                        // Append for subsequent pages
                        this.allTrustDetails = this.allTrustDetails.concat(data.trust_instances);
                    }
                }
                
                // Update pagination state
                this.hasMoreInstances = data.has_more || false;
                
            } catch (err) {
                console.error('Error loading more trust instances:', err);
                // You might want to show an error message to the user here
            } finally {
                this.loadingMore = false;
            }
        }
    },
    template: `
        <ui-panel-accordion color="white" innerColor="light-slate">
            <template #trigger>
                <ui-layout direction="column" gap="1" class="grow">
                    <ui-heading tag="h3" size="md">{{ relationship.trust_domain.hostname}}</ui-heading>
                    <span class="scanned-text">
                        First Scanned: {{ formatDate(relationship.trust_domain.first_scanned_at) }} | 
                        Last Scanned: {{ formatDate(relationship.trust_domain.last_scanned_at) }}
                    </span>
                </ui-layout>

                <ui-layout gap="2" align="center" wrap>
                    <!-- <ui-badge :label="trustLevelLabel" fill :color="trustColor(highestTrustLevel)" /> -->

                    <ui-badge :label="trustCountsLabel" fill/>

                    <ui-badge v-if="vulnerabilityCount" :label="vulnCountsLabel" fill :color="vulnCountsLabel > 0 ? 'red' : null" />

                    <!-- Chevron handled by ui-panel-accordion -->
                </ui-layout>
            </template>

            <ui-layout direction="column" v-if="vulnerabilityCount">
                <ui-heading tag="h4" size="md">Vulnerabilities</ui-heading>
                <ui-layout direction="column" gap="2">
                    <ui-layout gap="2" wrap v-for="vuln in relationship.vulnerabilities" :key="vuln.id || vuln.name" class="vulnerability-item">
                        <span>{{ vuln.name || vuln.title }}</span>
                        <span v-if="vuln.impact">{{ vuln.impact }}</span>
                    </ui-layout>
                </ui-layout>
                <ui-heading tag="span" v-if="relationship.highest_vulnerability_impact">
                    Highest Impact: {{ relationship.highest_vulnerability_impact }}
                </ui-heading>
            </ui-layout>

            <ui-layout direction="column">
                <ui-layout align="center" gap="2">
                    <ui-heading tag="h4" size="md">Trust Instances</ui-heading>
                    <ui-badge 
                        v-if="totalInstancesCount > 0"
                        :label="totalInstancesCount > trustInstancesCount ? 'Showing ' + trustInstancesCount + ' of ' + totalInstancesCount : totalInstancesCount + ' instance' + (totalInstancesCount !== 1 ? 's' : '')"
                        color="blue"
                        fill
                    />
                </ui-layout>
                <template v-if="allTrustDetails && allTrustDetails.length">
                    <apck-trust-instance-details
                        v-for="(instance, index) in allTrustDetails"
                        :key="index"
                        :instance="instance"
                    />
                    <!-- Inline Load More Button -->
                    <ui-layout v-if="hasMoreInstances" justify="center" direction="column" align="center" gap="2" class="mt-2">
                        <ui-button
                            :label="loadingMore ? 'Loading...' : 'Load More Instances'"
                            @click="loadMoreInstances"
                            :disabled="loadingMore"
                            type="outline"
                            size="sm"
                        />
                        <span class="text-sm text-secondary">
                            {{ trustInstancesCount }} of {{ totalInstancesCount }} loaded
                        </span>
                    </ui-layout>
                    <!-- View All Button (alternative to inline loading) -->
                    <ui-layout v-else-if="totalInstancesCount > 10" justify="center" class="mt-2">
                        <ui-button
                            label="View Full Relationship Details"
                            :url="'/trust/relationship/' + relationship.relationship_id"
                            type="outline"
                            size="sm"
                        />
                    </ui-layout>
                </template>
                <span v-else>No detailed trust information available.</span>
            </ui-layout>
        </ui-panel-accordion>
    `
};

/**
 * Main Third-party Details Component
 * Complete view showing all information about a third-party
 */
export const ApckTrustThirdpartyDetails = {
    components: {
        'apck-trust-level-badge': ApckTrustLevelBadge,
        'apck-country-info': ApckCountryInfo,
        'apck-thirdparty-favicon': ApckThirdpartyFavicon,
        'apck-domain-relationship': ApckDomainRelationship
    },
    props: {
        thirdpartyId: {
            type: String,
            required: true
        }
    },
    data: function() {
        return {
            loading: false,
            error: null,
            thirdpartyData: null,
            refreshInterval: null,
            relationships: [],
            relationshipsLoading: false,
            relationshipsPage: 1,
            relationshipsPerPage: 50,
            relationshipsTotalCount: 0,
            relationshipsHasMore: false
        };
    },
    computed: {
        hasData: function() {
            return this.thirdpartyData && this.thirdpartyData.success;
        },
        thirdparty: function() {
            return this.hasData ? this.thirdpartyData.thirdparty : {};
        },
        favicon: function() {
            return this.hasData ? this.thirdpartyData.favicon : {};
        },
        linkedDomains: function() {
            return this.hasData ? this.thirdpartyData.linked_domains : [];
        },
        relationshipsCount: function() {
            return this.hasData ? this.thirdpartyData.relationships_count : 0;
        },
        totalTrustInstances: function() {
            if (this.hasData && this.thirdpartyData.trust_instances_count !== undefined) {
                return this.thirdpartyData.trust_instances_count;
            }
            return this.relationships.reduce((total, rel) => {
                return total + (rel.trust_details?.length || 0);
            }, 0);
        },
        trustLevelSummary: function() {
            // Use summary from API if available, otherwise calculate from loaded relationships
            if (this.hasData && this.thirdpartyData.trust_level_summary) {
                const summary = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0, 0: 0 };
                Object.keys(this.thirdpartyData.trust_level_summary).forEach(level => {
                    summary[level] = this.thirdpartyData.trust_level_summary[level];
                });
                return summary;
            }
            // Fallback for backward compatibility
            const summary = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0, 0: 0 };
            this.relationships.forEach(rel => {
                const level = rel.highest_trust_level?.level || 0;
                summary[level]++;
            });
            return summary;
        },
        activeChecksConfigUrl() {
            return `/trust/active-monitor/config/add?url=${this.thirdparty.origin_url}&check_headers=true&check_changes=true`
        }
    },
    methods: {
        trustColor(level) {
            const colors = {
                5: 'red',
                4: 'orange',
                3: 'yellow',
                2: 'green',
                1: 'blue',
                0: ''
            };
            return colors[level] || '';
        },
        getTrustLevelName: function(level) {
            const levelNames = {
                5: 'HIGHEST',
                4: 'HIGH',
                3: 'MEDIUM',
                2: 'LOW',
                1: 'INFORMATIONAL',
                0: 'INVESTIGATE'
            };
            return levelNames[level] || 'UNKNOWN';
        },
        fetchData: function() {
            this.loading = true;
            this.error = null;

            fetch(`/trust/thirdparty/${this.thirdpartyId}/details`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json();
                })
                .then(data => {
                    this.thirdpartyData = data;
                    if (!data.success) {
                        this.error = data.message || 'Failed to load third-party details';
                    } else {
                        // Load initial relationships
                        this.loadRelationships();
                    }
                })
                .catch(error => {
                    console.error('Error fetching third-party details:', error);
                    this.error = error.message || 'Failed to load third-party details';
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadRelationships: function() {
            if (this.relationshipsLoading) return;

            this.relationshipsLoading = true;

            fetch(`/trust/thirdparty/${this.thirdpartyId}/relationships?page=${this.relationshipsPage}&per_page=${this.relationshipsPerPage}`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json();
                })
                .then(data => {
                    if (data.success) {
                        // Append new relationships to existing ones
                        this.relationships = this.relationships.concat(data.relationships);
                        this.relationshipsTotalCount = data.total_count;
                        this.relationshipsHasMore = data.has_more;
                    }
                })
                .catch(error => {
                    console.error('Error fetching relationships:', error);
                })
                .finally(() => {
                    this.relationshipsLoading = false;
                });
        },
        loadMoreRelationships: function() {
            this.relationshipsPage++;
            this.loadRelationships();
        },
        refresh: function() {
            this.fetchData();
        },
        formatDate: function(dateString) {
            if (!dateString) return 'N/A';
            dateString = `${dateString}000`; // Convert to milliseconds
            dateString = parseInt(dateString);
            return new Date(dateString).toLocaleString();
        },
        getDomainAge: function() {
            if (!this.thirdparty.domain_age) return 'Unknown';
            const age = this.thirdparty.domain_age;
            if (age.days) {
                return `${age.days} days`;
            }
            if (age.years) {
                return `${age.years} years`;
            }
            return 'Unknown';
        },
        startAutoRefresh: function() {
            // Refresh every 5 minutes
            this.refreshInterval = setInterval(this.fetchData, 5 * 60 * 1000);
        },
        stopAutoRefresh: function() {
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
                this.refreshInterval = null;
            }
        },
        trustRelationshipLabel(count) {
            return `${String(count)} relationship${String(count) > 1 ? 's' : ''}`
        },
        handleSuppressed(data) {
            // Navigate back to thirdparties list after suppression
            window.location.href = '/trust/thirdparties';
        }
    },
    mounted: function() {
        this.fetchData();
        this.startAutoRefresh();
    },
    beforeDestroy: function() {
        this.stopAutoRefresh();
    },
    watch: {
        thirdpartyId: function() {
            // Reset relationships state when switching thirdparties
            this.relationships = [];
            this.relationshipsPage = 1;
            this.relationshipsHasMore = false;
            this.fetchData();
        }
    },
    template: `
        <ui-page :title="'Third-party Details: ' + (thirdparty.hostname || 'Loading...')" stickyTitle>
            <template #title-bar-buttons>
                <apck-quick-suppress-button
                    v-if="hasData && thirdparty.hostname"
                    :hostname="thirdparty.hostname"
                    suppressionType="thirdparty"
                    buttonType="outline"
                    buttonColor="yellow"
                    v-tooltip="'Suppress this third party from showing up in the list and add it to the suppression control for the future'"
                    @suppressed="handleSuppressed"
                />
                <apck-convert-to-domain-button
                    v-if="hasData && thirdpartyId && thirdparty.hostname"
                    :thirdpartyId="thirdpartyId"
                    :hostname="thirdparty.hostname"
                    buttonType="outline"
                    buttonColor="blue"
                    v-tooltip="'Convert to a monitored domain, I manage this third party'"
                />
                <ui-button
                    v-if="$permissions.enable_active_checks_service"
                    label="Enable Active Monitor"
                    :url="activeChecksConfigUrl"
                />
            </template>
            <template #content>
<!--                <ui-spinner :show="loading && !hasData" theme="dark" />-->

                <div v-if="error" class="error-container">
                    <apck-panel title="Error" icon="fa-exclamation-triangle">
                        <p>{{ error }}</p>
                        <ui-button
                            iconLeft="fa-refresh"
                            label="Refresh"
                            type="ghost"
                            hideLabel
                            size="sm"
                            :disabled="loading"
                            @click.prevent="refresh"
                        />
                    </apck-panel>
                </div>

                <ui-layout v-else-if="hasData" type="grid" cols="2" class="thirdparty-details-content">

                    <!-- Third-party Overview -->
                    <apck-panel title="Third-party Overview" icon="fa-globe" class="thirdparty-overview text--ellipsis">
                        <template v-slot:menu>
                            <ui-button
                                iconLeft="fa-refresh"
                                label="Refresh"
                                type="ghost"
                                hideLabel
                                size="sm"
                                :disabled="loading"
                                @click.prevent="refresh"
                            />
                        </template>

                        <ui-layout direction="column" gap="3">
                            <ui-layout gap="2">
                                <span class="text--secondary">Hostname:</span>
    
                                <ui-heading tag="span" class="text--ellipsis">
                                    {{ thirdparty.hostname }}
                                </ui-heading>
                            </ui-layout>

                            <template v-if="thirdparty.origin_url">
                                <ui-layout gap="2">
                                    <span class="text--secondary col-span-full">Origin URL:</span>
    
                                    <ui-button
                                        :label="thirdparty.origin_url"
                                        :url="thirdparty.origin_url"
                                        type="link"
                                        external
                                        iconRight="fa-external-link"
                                        size="sm"
                                        class="col-span-full"
                                        ellipsis
                                    />
                                </ui-layout>
                            </template>

                            <template v-if="thirdparty.parent_domain">
                                <ui-layout gap="2" >
                                    <span class="text--secondary">Apex Domain:</span>
    
                                    <ui-heading tag="span">{{ thirdparty.parent_domain }}</ui-heading>
                                </ui-layout>
                            </template>

                            <ui-layout gap="2" >
                                <span class="text--secondary">Service Name:</span>
    
                                <ui-heading tag="span">{{ thirdparty.service_name || 'Unknown' }}</ui-heading>
                            </ui-layout>

                            <ui-layout gap="2" >
                                <span class="text--secondary">Domain Age:</span>
    
                                <ui-heading tag="span">{{ getDomainAge() }}</ui-heading>
                            </ui-layout>

                            <ui-layout gap="2" >
                                <span class="text--secondary">NX Domain: Takeover Risk</span>
    
                                <ui-badge
                                    fill
                                    :label="thirdparty.nxdomain ? 'Yes' : 'No'"
                                    :color="thirdparty.nxdomain ? 'yellow' : 'green'"
                                />
                            </ui-layout>

                            <!--
                            <ui-layout gap="2" >
                                <span class="text--secondary">Trusted to CNAME:</span>
    
                                <ui-badge
                                    fill
                                    :label="thirdparty.i_am_trusted_to_cname ? 'Yes' : 'No'"
                                    :color="thirdparty.i_am_trusted_to_cname ? 'yellow' : 'green'"
                                />
                            </ui-layout>
                            -->

                            <template v-if="thirdparty.country_info">
                                <ui-layout gap="2" >
                                    <span class="text--secondary">Location:</span>
    
                                    <apck-country-info :country-info="thirdparty.country_info" />
                                </ui-layout>
                            </template>

                            <template v-if="thirdparty.cnames?.length > 0">
                                <ui-layout gap="2" >
                                    <span class="text--secondary">CNAMEs:</span>
                                    
                                    <ui-layout class="text--ellipsis" gap="1" tag="ul" direction="column">
                                        <ui-heading class="text--ellipsis" tag="li" v-for="cname in thirdparty.cnames" :key="cname">
                                            {{ cname }}
                                        </ui-heading>
                                    </ui-layout>
                                </ui-layout>
                            </template>
                        </ui-layout>

                        <ui-spinner :show="loading" theme="dark" />
                    </apck-panel>

                    <!-- Summary Statistics -->
                    <apck-panel title="Trust Summary" icon="fa-chart-bar">
                        <ui-layout direction="column">
                            <ui-layout justify="stretch" wrap>
                                <ui-panel-base color="light-slate" direction="column" align="center" justify="center" gap="0">
                                    <ui-heading tag="h3" size="md">{{ linkedDomains.length }}</ui-heading>
    
                                    <span class="text--secondary">Linked Domains</span>
                                </ui-panel-base>
    
                                <ui-panel-base color="light-slate" direction="column" align="center" justify="center" gap="0">
                                    <ui-heading tag="h3" size="md">{{ relationships.length }}</ui-heading>
    
                                    <span class="text--secondary">Relationships</span>
                                </ui-panel-base>
    
                                <ui-panel-base color="light-slate" direction="column" align="center" justify="center" gap="0">
                                    <ui-heading tag="h3" size="md">{{ totalTrustInstances }}</ui-heading>
    
                                    <span class="text--secondary">Trust Instances</span>
                                </ui-panel-base>
                            </ui-layout>

                            <ui-layout justify="stretch" align="stretch" wrap>
                                <ui-panel-base color="light-slate" direction="column" align="start" justify="start" class="col-span-full">
                                    <ui-heading tag="h3">Trust Level Breakdown</ui-heading>
    
                                    <ui-layout align="center" wrap>
                                        <template v-for="(count, level) in trustLevelSummary" >
                                            <ui-layout v-if="count > 0" gap="2" align="center">
                                                <ui-badge :label="String(level) + ' - ' + getTrustLevelName(level)" fill :color="trustColor(level)" />
    
                                                <ui-badge :label="trustRelationshipLabel(count)" />
                                            </ui-layout>
                                        </template>
                                    </ui-layout>
    
    <!--                                <span v-else>No trust levels found</span>-->
                                </ui-panel-base>
                            </ui-layout>
                        </ui-layout>
                    </apck-panel>

                    <!-- Domain Relationships -->
                    <apck-panel title="Domain Relationships" icon="fa-sitemap" class="domain-relationships col-span-full">
                        <div class="relationships-container">
                            <div v-if="relationshipsCount === 0" class="no-relationships">
                                <p><em>No relationships found for this third-party</em></p>
                            </div>
                            <ui-layout v-else direction="column" gap="1" class="relationships-list">
                                <apck-domain-relationship
                                    v-for="relationship in relationships"
                                    :key="relationship.relationship_id"
                                    :relationship="relationship"
                                />
                                
                                <ui-layout v-if="relationshipsHasMore" direction="column" align="center" gap="2">
                                    <ui-button
                                        label="Load More"
                                        iconLeft="fa-chevron-down"
                                        :disabled="relationshipsLoading"
                                        @click="loadMoreRelationships"
                                    />
                                    <span class="text--secondary">
                                        Showing {{ relationships.length }} of {{ relationshipsTotalCount }} relationships
                                    </span>
                                </ui-layout>
                                
                                <ui-spinner v-if="relationshipsLoading" :show="true" theme="dark" />
                            </ui-layout>
                        </div>
                    </apck-panel>
                </ui-layout>
            </template>
        </ui-page>
    `
};
